import { useEffect, useState, useCallback } from "react";
import { api, errText } from "../lib/api";
import { Loading, Empty } from "../components/common";
import { StoreItemCard, ItemVisual, PriceTag } from "../components/storeui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Coins, Search, Store as StoreIcon, Check } from "lucide-react";
import { toast } from "sonner";

const KINDS = [["all", "All"], ["animation", "Animations"], ["background", "Backgrounds"], ["badge", "Badges"], ["frame", "Frames"]];
const SORTS = [["featured", "Featured"], ["newest", "Newest"], ["popular", "Popular"], ["price_low", "Price ↑"], ["price_high", "Price ↓"]];

function Section({ title, items, onOpen, onBuy }) {
  if (!items?.length) return null;
  return (
    <div className="mb-8">
      <h2 className="font-display font-bold text-lg mb-3">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => <StoreItemCard key={it.id} item={it} onClick={() => onOpen(it)} onBuy={onBuy} testid={`store-item-${it.id}`} />)}
      </div>
    </div>
  );
}

export default function Store() {
  const [home, setHome] = useState(null);
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("featured");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [balance, setBalance] = useState(0);
  const [detail, setDetail] = useState(null);
  const [buying, setBuying] = useState(false);

  const browsing = kind !== "all" || !!q.trim() || sort !== "featured";

  const loadWallet = useCallback(() => { api.get("/wallet").then(({ data }) => setBalance(data.available)).catch(() => {}); }, []);
  useEffect(() => { api.get("/store/home").then(({ data }) => setHome(data)).catch(() => {}); loadWallet(); }, [loadWallet]);

  const loadItems = useCallback(() => {
    if (!browsing) { setItems(null); return; }
    const params = new URLSearchParams();
    if (kind !== "all") params.set("kind", kind);
    if (q.trim()) params.set("q", q.trim());
    params.set("sort", sort);
    api.get(`/store/items?${params}`).then(({ data }) => setItems(data)).catch(() => {});
  }, [kind, sort, q, browsing]);
  useEffect(() => { const t = setTimeout(loadItems, 250); return () => clearTimeout(t); }, [loadItems]);

  const refresh = () => { api.get("/store/home").then(({ data }) => setHome(data)); loadItems(); loadWallet(); };

  const buy = async (item) => {
    setBuying(true);
    try {
      await api.post(`/store/items/${item.id}/purchase`);
      toast.success(`${item.name} added to your Collection`);
      setDetail((d) => d ? { ...d, owned: true } : d);
      refresh();
    } catch (e) { toast.error(errText(e)); } finally { setBuying(false); }
  };

  return (
    <div className="px-4 md:px-6 py-5 max-w-[1100px] mx-auto" data-testid="store-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl flex items-center gap-2"><StoreIcon className="text-gold" size={26} />VirtuStars <span className="text-gold-grad">Store</span></h1>
          <p className="text-sm text-muted-foreground mt-1">Premium animations, backgrounds, badges & frames for your public profile.</p>
        </div>
        <div className="flex items-center gap-1.5 surface rounded-full px-4 h-10 text-gold font-bold" data-testid="store-balance"><Coins size={16} />{balance.toLocaleString()} VC</div>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-full px-3 flex-1 min-w-[180px] max-w-xs">
          <Search size={15} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the store…" className="bg-transparent flex-1 py-2 text-sm outline-none" data-testid="store-search-input" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="surface-2 border border-white/10 rounded-full px-3 py-2 text-sm" data-testid="store-sort">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-1 mb-6 surface rounded-full p-1 w-fit overflow-x-auto" data-testid="store-kind-tabs">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`store-kind-${k}`}
            className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${kind === k ? "btn-blue text-white" : "text-muted-foreground hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {browsing ? (
        items === null ? <Loading /> : items.length === 0 ? <Empty icon={StoreIcon} title="No items found" hint="Try a different category or search." /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map((it) => <StoreItemCard key={it.id} item={it} onClick={() => setDetail(it)} onBuy={buy} testid={`store-item-${it.id}`} />)}
          </div>
        )
      ) : !home ? <Loading /> : (
        <>
          <Section title="Featured Drops" items={home.featured} onOpen={setDetail} onBuy={buy} />
          <Section title="New Arrivals" items={home.new_arrivals} onOpen={setDetail} onBuy={buy} />
          <Section title="Trending Animations" items={home.animations} onOpen={setDetail} onBuy={buy} />
          <Section title="Exclusive Backgrounds" items={home.backgrounds} onOpen={setDetail} onBuy={buy} />
          <Section title="Badge Collection" items={home.badges} onOpen={setDetail} onBuy={buy} />
          <Section title="Profile Frames" items={home.frames} onOpen={setDetail} onBuy={buy} />
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="store-item-detail">
          {detail && <>
            <DialogHeader><DialogTitle className="font-display">{detail.name}</DialogTitle><DialogDescription className="capitalize">{detail.kind} · Profile customization</DialogDescription></DialogHeader>
            <ItemVisual item={detail} size="h-44" />
            <p className="text-sm text-muted-foreground mt-1">{detail.description}</p>
            <div className="flex items-center justify-between mt-2">
              <PriceTag price={detail.price} />
              {detail.sold_count > 0 && <span className="text-xs text-muted-foreground">{detail.sold_count.toLocaleString()} owned</span>}
            </div>
            {detail.owned ? (
              <div className="w-full h-11 rounded-xl surface-2 border border-emerald-400/30 text-emerald-400 flex items-center justify-center gap-1 mt-2"><Check size={16} />You own this — equip it in My Collection</div>
            ) : (
              <button onClick={() => buy(detail)} disabled={buying} data-testid="store-detail-buy" className="w-full h-11 rounded-xl btn-blue font-semibold mt-2 flex items-center justify-center gap-1">
                {buying ? "Processing…" : detail.price > 0 ? <>Buy for <Coins size={15} />{detail.price.toLocaleString()} VC</> : "Get for free"}
              </button>
            )}
            <p className="text-[11px] text-muted-foreground text-center mt-1">Applied to your public VirtuStars profile. Collectible — not a verified or staff credential.</p>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
